/**
 * ADR-0220 §3.2 — Initiatives list (`/app/initiatives`), built to the confirmed
 * mock-up `#list`. The shell is TenantStudies.tsx's (PageLayout, `card
 * adminList`, `tableWrap tableWrapFrame` + useViewportFrame); the toolbar and
 * the per-account view memory are ActionItemsList.tsx's (spec E11). Every word
 * shown — health, its reason, the lifecycle — is served by the hub; nothing is
 * derived here.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useViewportFrame } from "../hooks/useViewportFrame";
import { apiFetch } from "../lib/api.js";
import { applyViewState, readViewState, serializeViewState, viewStateInUrl, writeViewState } from "../lib/listViewMemory.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";

// ── the served shapes (hub src/initiatives/types.ts) ─────────────────────────

export type Lifecycle = "proposed" | "active" | "delivered" | "closed" | "stopped";
export type HealthState = "needs_attention" | "stale" | "behind" | "on_track" | "no_measure";

/** The measure as the hub serves it (src/initiatives/types.ts `MeasureView`). Ids ride for the picker and the link, never rendered. */
export type MeasureView = {
  operation_id: string | null;
  node_id: string;
  column: string;
  pick: "last_row" | "sum" | "avg" | "max" | "min";
  direction: "lower_is_better" | "higher_is_better";
  baseline: number;
  baseline_as_of: string;
  target: number;
  name: string;
  operation: { id: string; name: string; href: string | null } | null;
  step_label: string;
  reading: { current: number | null; as_of: string | null; run_id: string | null; unavailable_reason: string | null };
  progress: { fraction: number; elapsed: number | null; sentence: string } | null;
};

/** A measure number as people read it: up to two decimals, thousands grouped. */
export function fmtMeasure(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
}
export type Health = { state: HealthState | null; reason: string | null };
export type TeamRef = { id: string; name: string };

export type InitiativeListItem = {
  id: string;
  /** Amendment 3: the ID shown beside the name (I-006); the URL keeps `id`. */
  ref: string;
  name: string;
  lifecycle: Lifecycle;
  health: Health;
  /** Phase 3: the declared measure with its reading (hub `MeasureView`); null when none is declared. */
  measure: MeasureView | null;
  owners: { name: string }[];
  teams: TeamRef[];
  open_issues: number;
  awaiting_confirmation: number;
  last_activity_at: string | null;
  planned_start: string | null;
  actual_start: string | null;
  planned_end: string | null;
  actual_end: string | null;
};

export const LIFECYCLE_WORDS: Record<Lifecycle, string> = {
  proposed: "Proposed",
  active: "Active",
  delivered: "Delivered",
  closed: "Closed",
  stopped: "Stopped",
};

export const HEALTH_WORDS: Record<HealthState, string> = {
  needs_attention: "Needs attention",
  stale: "Stale",
  behind: "Behind",
  on_track: "On track",
  no_measure: "No measure",
};

const DATE_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const LOCAL_DATE_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

/** A served date ('YYYY-MM-DD', the calendar date as written) or a timestamp (shown in the viewer's zone) → "Sep 1, 2026". */
export function fmtDay(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return DATE_FMT.format(new Date(`${value}T00:00:00Z`));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : LOCAL_DATE_FMT.format(d);
}

/** The list's Health cell (§3.2): a dot and the served word; the reason in `title`. */
export function healthDot(h: Health): JSX.Element {
  if (h.state === null) return <span className="statusDot statusDotMuted">—</span>;
  const cls = h.state === "on_track" ? "statusDotOk" : h.state === "no_measure" ? "statusDotMuted" : "statusDotWarn";
  return (
    <span className={`statusDot ${cls}`} title={h.reason ?? undefined}>
      {HEALTH_WORDS[h.state]}
    </span>
  );
}

export function measureCell(m: InitiativeListItem["measure"]): JSX.Element {
  if (!m) return <span className="muted">—</span>;
  const current = m.reading.current;
  return (
    <span title={current === null ? (m.reading.unavailable_reason ?? undefined) : undefined}>
      {m.name}: {fmtMeasure(current)} → {fmtMeasure(m.target)}
    </span>
  );
}

export const EMPTY_TEXT = "No initiatives yet. Open one with + New initiative, or from an Atlas-connected session (create_initiative).";
export const NO_MATCH_TEXT = "No initiatives match. Clear the search or the team, or check Show closed.";

/** The table, pure, so the tests render it without a router state or a fetch. */
export function InitiativesTable(props: { items: InitiativeListItem[] }): JSX.Element {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Health</th>
          <th>Lifecycle</th>
          <th>Measure</th>
          <th>Owners</th>
          <th>Teams</th>
          <th>Open issues</th>
          <th>Last activity</th>
          <th>Planned end</th>
        </tr>
      </thead>
      <tbody>
        {props.items.map((i) => (
          <tr key={i.id}>
            <td className="mono">{i.ref}</td>
            <td>
              <Link className="adminListName" to={`/app/initiatives/${encodeURIComponent(i.id)}`}>
                {i.name}
              </Link>
            </td>
            <td>{healthDot(i.health)}</td>
            <td>{LIFECYCLE_WORDS[i.lifecycle]}</td>
            <td>{measureCell(i.measure)}</td>
            <td>{i.owners.length === 0 ? <span className="muted">—</span> : i.owners.map((o) => o.name).join(", ")}</td>
            <td>{i.teams.length === 0 ? <span className="muted">—</span> : i.teams.map((t) => t.name).join(", ")}</td>
            <td className={i.open_issues === 0 ? "muted" : undefined} title={i.awaiting_confirmation > 0 ? `${i.awaiting_confirmation} awaiting confirmation` : undefined}>
              {i.open_issues}
            </td>
            <td>{fmtDay(i.last_activity_at)}</td>
            <td>
              {i.actual_end ? (
                <>
                  {fmtDay(i.actual_end)} <span className="muted">(ended)</span>
                </>
              ) : (
                fmtDay(i.planned_end)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Every URL param that makes up "the view I had" (spec §3.2). */
const REMEMBERED_PARAMS = ["show_closed", "q", "team_id"] as const;

function viewMemoryKey(accountId: string): string {
  return `atlas.initiatives.view:${accountId}`;
}

export function TenantInitiatives(props: { entitlement?: string; callerAccountId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showClosed = searchParams.get("show_closed") === "1";
  const q = searchParams.get("q") ?? "";
  const teamId = searchParams.get("team_id") ?? "";
  const [searchDraft, setSearchDraft] = useState(q);
  const [items, setItems] = useState<InitiativeListItem[]>([]);
  const [teams, setTeams] = useState<TeamRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  useViewportFrame(gridRef, { active: !loading && !error, bottomGap: 24 + 16 + 1 });
  const canCreate = props.entitlement === "builder" || props.entitlement === "admin";

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === "") p.delete(k);
            else p.set(k, v);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---- View memory (session, per account) — the ActionItemsList pattern ----
  const memoryKey = viewMemoryKey(props.callerAccountId);
  const [memoryResolved, setMemoryResolved] = useState(false);
  const pendingRestoreRef = useRef<string | null>(null);
  useEffect(() => {
    const stored = readViewState(memoryKey);
    if (stored && !viewStateInUrl(searchParams, REMEMBERED_PARAMS)) {
      pendingRestoreRef.current = stored;
      setSearchParams((prev) => applyViewState(prev, stored, REMEMBERED_PARAMS), { replace: true });
    }
    setMemoryResolved(true);
    // Mount-only: the URL the user arrived on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);
  useEffect(() => {
    if (!memoryResolved) return;
    const serialized = serializeViewState(searchParams, REMEMBERED_PARAMS);
    if (pendingRestoreRef.current !== null) {
      if (serialized !== pendingRestoreRef.current) return;
      pendingRestoreRef.current = null;
    }
    writeViewState(memoryKey, serialized);
  }, [searchParams, memoryKey, memoryResolved]);
  useEffect(() => setSearchDraft(q), [q]);

  // The Team filter's choices: the tenant's active groups (the RASCI candidates' groups).
  useEffect(() => {
    void apiFetch<{ candidates: Array<{ id: string; kind: string; display_name: string }> }>("/api/tenant/rasci-candidates")
      .then((r) => setTeams(r.candidates.filter((c) => c.kind === "group").map((c) => ({ id: c.id, name: c.display_name }))))
      .catch(() => setTeams([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (showClosed) qs.set("show_closed", "1");
      if (q) qs.set("q", q);
      if (teamId) qs.set("team_id", teamId);
      const data = await apiFetch<{ initiatives: InitiativeListItem[] }>(`/api/tenant/initiatives${qs.toString() ? `?${qs}` : ""}`);
      setItems(data.initiatives);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [showClosed, q, teamId]);

  useEffect(() => {
    if (memoryResolved) void load();
  }, [load, memoryResolved]);

  const applySearch = () => {
    if (searchDraft.trim() !== q) setParams({ q: searchDraft.trim() || null });
  };
  const filtered = showClosed || q !== "" || teamId !== "";

  return (
    <PageLayout
      title="Initiatives"
      actions={
        canCreate ? (
          <Link className="btn btnPrimary" to="/app/initiatives/new">
            + New initiative
          </Link>
        ) : undefined
      }
    >
      <div className="card adminList">
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <div className="toolbarGroup">
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={showClosed} onChange={() => setParams({ show_closed: showClosed ? null : "1" })} />
              Show closed
            </label>
            <select
              className="select"
              aria-label="Team"
              value={teamId}
              onChange={(e) => setParams({ team_id: e.target.value || null })}
              style={{ width: "auto", fontSize: 13 }}
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="search"
              placeholder="Search by name or ID"
              aria-label="Search by name"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onBlur={applySearch}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              style={{ width: 220, fontSize: 13 }}
            />
          </div>
        </div>
        {error ? <ErrorState detail={error} /> : null}
        {!error && loading ? <LoadingState /> : null}
        {!error && !loading && items.length === 0 ? <div className="adminListEmpty">{filtered ? NO_MATCH_TEXT : EMPTY_TEXT}</div> : null}
        {!error && !loading && items.length > 0 ? (
          <div className="tableWrap tableWrapFrame" ref={gridRef}>
            <InitiativesTable items={items} />
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}

/** Amendment 3: an initiative named everywhere as its ID beside its name — "I-006 Reduce setup times". */
export function initiativeLabel(i: { ref?: string | null; name: string }): string {
  return i.ref ? `${i.ref} ${i.name}` : i.name;
}

/** The reverse direction (spec §3.5): the initiatives a study or an Atlas Report serves, as links, or —. Read-only. */
export function InitiativeLinks(props: { initiatives: Array<{ id: string; ref?: string; name: string }> | undefined }): JSX.Element {
  const list = props.initiatives ?? [];
  if (list.length === 0) return <span className="muted">—</span>;
  return (
    <>
      {list.map((i, n) => (
        <span key={i.id}>
          {n > 0 ? ", " : ""}
          <Link to={`/app/initiatives/${encodeURIComponent(i.id)}`}>{initiativeLabel(i)}</Link>
        </span>
      ))}
    </>
  );
}
